/**
 * Combat sounds, synthesised with Web Audio (no files to load): a whoosh when
 * a limb is thrown, and a distinct sound when a strike meets someone — a thump
 * with a crack when it lands, a dull knock when a guard takes it, a bright
 * clack on a parry. Contact is only ever known from the server's hit event, so
 * the impact is the confirmation that a strike connected: a whoosh with no
 * impact after it was a miss.
 *
 * Sounds are placed on the ground plane relative to the listener (the local
 * fighter, facing the camera's yaw): quieter with distance, panned left/right.
 */
import type { Vec3 } from "@meleeroyale/sim";
import type { ClientView } from "../net/game-client.ts";

const SETTINGS_KEY = "melee.sound";

export interface SoundSettings {
  /** 0 .. 1 */
  volume: number;
}

export function loadSoundSettings(): SoundSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<SoundSettings>;
      if (typeof parsed.volume === "number") return { volume: clamp01(parsed.volume) };
    }
  } catch {
    /* private mode or blocked storage: defaults are fine */
  }
  return { volume: 0.7 };
}

function saveSoundSettings(settings: SoundSettings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    /* the setting just will not persist */
  }
}

export interface ImpactSound {
  /** 0 (a touch) .. 1 (a big share of the part it hit). */
  severity: number;
  blocked: boolean;
  parried: boolean;
  /** The local fighter threw it or took it: always clearly audible. */
  mine: boolean;
  /** Seconds from now, to line the sound up with what is drawn. */
  delay: number;
}

const QUIET = 0.0001;

export class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private volume: number;
  private listenerX = 0;
  private listenerZ = 0;
  private listenerYaw = 0;

  constructor() {
    this.volume = loadSoundSettings().volume;
    // Browsers only let audio start after a gesture; any click or key will do.
    document.addEventListener("pointerdown", this.unlock, true);
    document.addEventListener("keydown", this.unlock, true);
    this.unlock();
  }

  dispose(): void {
    document.removeEventListener("pointerdown", this.unlock, true);
    document.removeEventListener("keydown", this.unlock, true);
    void this.ctx?.close().catch(() => {});
    this.ctx = null;
  }

  private unlock = (): void => {
    if (!this.ctx) {
      try {
        this.ctx = new AudioContext();
      } catch {
        return;
      }
      this.master = this.ctx.createGain();
      this.master.gain.value = this.volume;
      this.master.connect(this.ctx.destination);
      this.noise = whiteNoise(this.ctx);
    }
    if (this.ctx.state === "suspended") void this.ctx.resume().catch(() => {});
  };

  get level(): number {
    return this.volume;
  }

  setVolume(volume: number): void {
    this.volume = clamp01(volume);
    if (this.master) this.master.gain.value = this.volume;
    saveSoundSettings({ volume: this.volume });
  }

  setListener(x: number, z: number, yaw: number): void {
    this.listenerX = x;
    this.listenerZ = z;
    this.listenerYaw = yaw;
  }

  /** A limb cutting the air. Legs are lower and longer; a charge is louder. */
  whoosh(at: Vec3, leg: boolean, charge: number, delay: number): void {
    const ctx = this.running();
    if (!ctx || !this.noise || this.volume <= 0) return;
    const { gain, pan } = this.place(at);
    const t0 = ctx.currentTime + delay;
    const dur = (leg ? 0.26 : 0.19) + 0.07 * charge;
    const f0 = leg ? 260 : 420;
    const f1 = leg ? 1200 : 2300;
    const src = this.noiseSource(ctx);
    const band = ctx.createBiquadFilter();
    band.type = "bandpass";
    band.Q.value = 1.3;
    band.frequency.setValueAtTime(f0, t0);
    band.frequency.exponentialRampToValueAtTime(f1, t0 + dur * 0.5);
    band.frequency.exponentialRampToValueAtTime(f0 * 1.5, t0 + dur);
    const env = envelope(ctx, t0, 0.02 + dur * 0.35, dur, (0.16 + 0.2 * charge) * gain);
    src.connect(band).connect(env).connect(this.output(ctx, pan));
    src.start(t0, Math.random() * 0.5);
    src.stop(t0 + dur + 0.05);
  }

  /** A strike meeting a body: landed, blocked or parried. */
  impact(at: Vec3, hit: ImpactSound): void {
    const ctx = this.running();
    if (!ctx || !this.noise || this.volume <= 0) return;
    const placed = this.place(at);
    const gain = hit.mine ? Math.max(placed.gain, 0.9) : placed.gain;
    const out = this.output(ctx, hit.mine ? placed.pan * 0.5 : placed.pan);
    const t0 = ctx.currentTime + hit.delay;
    if (hit.parried) this.clack(ctx, out, t0, gain);
    else if (hit.blocked) this.knock(ctx, out, t0, gain);
    else this.thump(ctx, out, t0, gain, clamp01(hit.severity));
  }

  /** Landed: a pitched-down body thump under a short crack; heavier hits go lower and longer. */
  private thump(ctx: AudioContext, out: AudioNode, t0: number, gain: number, sev: number): void {
    const body = ctx.createOscillator();
    body.type = "sine";
    body.frequency.setValueAtTime(170 - 50 * sev, t0);
    body.frequency.exponentialRampToValueAtTime(42, t0 + 0.16 + 0.08 * sev);
    const bodyEnv = envelope(ctx, t0, 0.004, 0.2 + 0.14 * sev, (0.5 + 0.45 * sev) * gain);
    body.connect(bodyEnv).connect(out);
    body.start(t0);
    body.stop(t0 + 0.4);

    const crack = this.noiseSource(ctx);
    const high = ctx.createBiquadFilter();
    high.type = "highpass";
    high.frequency.value = 1400;
    const crackEnv = envelope(ctx, t0, 0.002, 0.045 + 0.03 * sev, (0.3 + 0.35 * sev) * gain);
    crack.connect(high).connect(crackEnv).connect(out);
    crack.start(t0, Math.random() * 0.5);
    crack.stop(t0 + 0.12);
  }

  /** Blocked: a short, dull knock on a forearm, no crack. */
  private knock(ctx: AudioContext, out: AudioNode, t0: number, gain: number): void {
    const tone = ctx.createOscillator();
    tone.type = "triangle";
    tone.frequency.setValueAtTime(300, t0);
    tone.frequency.exponentialRampToValueAtTime(140, t0 + 0.07);
    const toneEnv = envelope(ctx, t0, 0.003, 0.09, 0.35 * gain);
    tone.connect(toneEnv).connect(out);
    tone.start(t0);
    tone.stop(t0 + 0.15);

    const thud = this.noiseSource(ctx);
    const low = ctx.createBiquadFilter();
    low.type = "lowpass";
    low.frequency.value = 650;
    const thudEnv = envelope(ctx, t0, 0.002, 0.07, 0.4 * gain);
    thud.connect(low).connect(thudEnv).connect(out);
    thud.start(t0, Math.random() * 0.5);
    thud.stop(t0 + 0.12);
  }

  /** Parried: a bright clack, the strike turned aside. */
  private clack(ctx: AudioContext, out: AudioNode, t0: number, gain: number): void {
    const tone = ctx.createOscillator();
    tone.type = "square";
    tone.frequency.setValueAtTime(980, t0);
    tone.frequency.exponentialRampToValueAtTime(560, t0 + 0.06);
    const band = ctx.createBiquadFilter();
    band.type = "bandpass";
    band.frequency.value = 1300;
    band.Q.value = 2;
    const toneEnv = envelope(ctx, t0, 0.002, 0.08, 0.3 * gain);
    tone.connect(band).connect(toneEnv).connect(out);
    tone.start(t0);
    tone.stop(t0 + 0.12);

    const tick = this.noiseSource(ctx);
    const high = ctx.createBiquadFilter();
    high.type = "highpass";
    high.frequency.value = 3000;
    const tickEnv = envelope(ctx, t0, 0.001, 0.03, 0.25 * gain);
    tick.connect(high).connect(tickEnv).connect(out);
    tick.start(t0, Math.random() * 0.5);
    tick.stop(t0 + 0.06);
  }

  private running(): AudioContext | null {
    const ctx = this.ctx;
    return ctx && ctx.state === "running" ? ctx : null;
  }

  private noiseSource(ctx: AudioContext): AudioBufferSourceNode {
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    return src;
  }

  private output(ctx: AudioContext, pan: number): AudioNode {
    const panner = ctx.createStereoPanner();
    panner.pan.value = pan;
    if (this.master) panner.connect(this.master);
    return panner;
  }

  /** Distance attenuation and a left/right pan relative to where the camera looks. */
  private place(at: Vec3): { gain: number; pan: number } {
    const dx = at.x - this.listenerX;
    const dz = at.z - this.listenerZ;
    const dist = Math.hypot(dx, dz);
    // The camera's right for yaw `a` is (-cos a, 0, sin a) (see input/controls.ts).
    const right =
      dist > 1e-3 ? (dx * -Math.cos(this.listenerYaw) + dz * Math.sin(this.listenerYaw)) / dist : 0;
    return {
      gain: 1 / (1 + Math.max(0, dist - 1.5) / 4),
      pan: Math.max(-1, Math.min(1, right * 0.8)),
    };
  }
}

/**
 * Whooshes from the rendered view: a strike entering its throw is a rising
 * edge of `throwing` on that fighter. The local fighter is predicted, so its
 * whoosh plays now; remote fighters are drawn `remoteDelay` seconds in the
 * past, so theirs is scheduled that much later to match what is on screen.
 */
export class ThrowSounds {
  private readonly prev = new Map<number, { arm: boolean; leg: boolean; charge: number }>();

  update(view: ClientView, sfx: Sfx, remoteDelay: number): void {
    for (const f of view.fighters) {
      const tg = f.telegraph;
      const p = this.prev.get(f.id) ?? { arm: false, leg: false, charge: 0 };
      // The charge is visible while the limb loads; remember it for the throw that follows.
      const charge = tg.chargeLimb !== null ? tg.chargeLevel : p.charge;
      const delay = f.isLocal ? 0 : remoteDelay;
      if (f.alive && tg.throwingArm && !p.arm) {
        sfx.whoosh({ x: f.pos.x, y: 1.4, z: f.pos.z }, false, charge, delay);
      }
      if (f.alive && tg.throwingLeg && !p.leg) {
        sfx.whoosh({ x: f.pos.x, y: 0.7, z: f.pos.z }, true, charge, delay);
      }
      const throwing = tg.throwingArm || tg.throwingLeg;
      this.prev.set(f.id, {
        arm: tg.throwingArm,
        leg: tg.throwingLeg,
        charge: tg.chargeLimb !== null ? tg.chargeLevel : throwing ? p.charge : 0,
      });
    }
  }
}

/** Gain that rises to `peak` in `attack` s and dies away by `end` s after `t0`. */
function envelope(
  ctx: AudioContext,
  t0: number,
  attack: number,
  end: number,
  peak: number,
): GainNode {
  const env = ctx.createGain();
  env.gain.setValueAtTime(QUIET, t0);
  env.gain.exponentialRampToValueAtTime(Math.max(QUIET * 2, peak), t0 + attack);
  env.gain.exponentialRampToValueAtTime(QUIET, t0 + Math.max(end, attack + 0.01));
  return env;
}

function whiteNoise(ctx: AudioContext): AudioBuffer {
  const buffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
  return buffer;
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}
